import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ANSWER_SCALE, FRICTION_DIMENSIONS } from '../data/mock';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const QuestionCard = ({ 
  question, 
  questionNumber, 
  totalQuestions, 
  selectedAnswer, 
  onAnswerSelect, 
  onNext, 
  onPrevious,
  language = 'fr',
  canGoNext = true,
  canGoPrevious = true,
  isLastQuestion = false
}) => {
  const dimension = FRICTION_DIMENSIONS[question.dimension];
  const progress = (questionNumber / totalQuestions) * 100;

  return (
    <div className="min-h-screen bg-[#0f1c47] flex items-center justify-center p-4">
      <div className="max-w-xl mx-auto w-full">
        {/* Logo */}
        <div className="text-center mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_manager-inventory/artifacts/kqdhn43c_2024_10_make_it_matter.png" 
            alt="WEDO" 
            className="mx-auto h-12 object-contain"
          />
        </div>

        <Card className="bg-white border-0 shadow-2xl">
          <CardContent className="p-6 md:p-8">
            {/* Header avec numéro de question */}
            <div className="text-center mb-6">
              <p className="text-gray-500 text-sm font-medium mb-2">
                Question {questionNumber} / {totalQuestions}
              </p>
              <span 
                className="inline-block px-3 py-1 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: dimension.color }}
              >
                {dimension.name}
              </span>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mb-8">
              <div 
                className="h-full rounded-full transition-all duration-500 ease-out"
                style={{ 
                  width: `${progress}%`,
                  backgroundColor: dimension.color
                }}
              />
            </div>
            
            {/* Question */}
            <div className="text-center mb-8">
              <h2 className="text-lg md:text-xl font-semibold text-[#0f1c47] leading-relaxed">
                {question.question}
              </h2>
            </div>

            {/* Answer Scale - Boutons verticaux */}
            <div className="space-y-2 mb-8">
              {ANSWER_SCALE.map((option) => (
                <button
                  key={option.value}
                  onClick={() => onAnswerSelect(option.value)}
                  className={`
                    w-full p-3 rounded-lg text-left transition-all duration-200
                    flex items-center gap-3
                    ${selectedAnswer === option.value 
                      ? 'text-white shadow-md' 
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }
                  `}
                  style={selectedAnswer === option.value ? {
                    backgroundColor: dimension.color
                  } : {}}
                >
                  <div className={`
                    w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0
                    ${selectedAnswer === option.value 
                      ? 'border-white bg-white/20' 
                      : 'border-gray-300'
                    }
                  `}>
                    {selectedAnswer === option.value && (
                      <div className="w-3 h-3 rounded-full bg-white"></div>
                    )}
                  </div>
                  <span className="font-medium text-sm">{option.label}</span>
                </button>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex justify-between gap-4">
              <Button
                onClick={onPrevious}
                disabled={!canGoPrevious}
                variant="outline"
                className={`flex-1 py-5 ${!canGoPrevious ? 'opacity-50' : ''}`}
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Précédent
              </Button>
              
              <Button
                onClick={onNext}
                disabled={selectedAnswer === null || selectedAnswer === undefined}
                className="flex-1 py-5 text-white border-0"
                style={{
                  background: selectedAnswer !== null && selectedAnswer !== undefined
                    ? `linear-gradient(135deg, ${dimension.color}, #e62c86)`
                    : '#ccc'
                }}
              >
                {isLastQuestion ? 'Continuer' : 'Suivant'}
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QuestionCard;
